import { useState, useEffect } from 'react';
import { Input, Select } from 'antd'; // Make sure InputNumber is not used if you only need Input

// Assuming these imports from your @seims/shared are available
import area from '@seims/shared/src/data/Location';
import { util } from '@seims/shared'; // Assuming defaultSettingEnum.COUNTRY_CODE exists

const { Option } = Select;

// Helper to parse the full contact string (moved here for encapsulation)
// This is crucial for splitting "+27 670742496" into its parts
const parseContactString = (fullContactString) => {
    if (!fullContactString || typeof fullContactString !== 'string') {
        return { countryCode: '+27', phoneNumber: '' }; // Default
    }

    // Attempt to split by space, but also handle cases without a space
    const parts = fullContactString.split(' ');
    let parsedCode = '+27'; // Default
    let parsedNumber = fullContactString; // Default to full string initially

    if (parts.length > 1 && parts[0].startsWith('+')) {
        // Assume first part is code if it starts with '+' and there's a space
        parsedCode = parts[0];
        parsedNumber = parts.slice(1).join(''); // Join remaining parts in case of multiple spaces
    } else if (fullContactString.startsWith('+')) {
        // If no space but starts with '+', try to find a known country code
        const sortedAreaCodes = area.areaCodes.sort(util.setDynamicSort("text"));
        const matchingCode = sortedAreaCodes.find(code => fullContactString.startsWith(code.value));
        if (matchingCode) {
            parsedCode = matchingCode.value;
            parsedNumber = fullContactString.substring(matchingCode.value.length);
        } else {
             // Fallback: If no matching code found, assume it's just a number with default +27
             parsedCode = '+27'; // Keep default +27
             parsedNumber = fullContactString.replace(/^\+\d+/, ''); // Remove any leading +digits
        }
    } else {
        // If it doesn't start with '+', assume it's just a local number for default +27
        parsedCode = '+27';
        parsedNumber = fullContactString;
    }

    // Clean up parsedNumber: remove non-numeric chars and leading zeros if it's the number part
    parsedNumber = util.toNumeric(parsedNumber);

    return { countryCode: parsedCode, phoneNumber: parsedNumber };
};


export const CustomContactNumberInput = ({ value, onChange }) => { // Ant Design Form passes 'value' and 'onChange'
    // State to hold the internal parts of the contact number
    const [currentCountryCode, setCurrentCountryCode] = useState('+27'); // Default
    const [currentPhoneNumber, setCurrentPhoneNumber] = useState(''); // Default

    // Area codes list initialization
    const [areaCodeList, setAreaCodeList] = useState(() => {
        let initialCodes = [...area.areaCodes.sort(util.setDynamicSort("text"))];
        const defaultCodeObj = { value: "+27", text: "+27 (ZA)" };
        // Ensure default code is at the beginning if not already there
        if (!initialCodes.some(code => code.value === defaultCodeObj.value)) {
            initialCodes.unshift(defaultCodeObj);
        }
        return initialCodes;
    });

    // --- EFFECT TO INITIALIZE/UPDATE INTERNAL STATE FROM 'value' PROP ---
    useEffect(() => {
        // 'value' from Form.Item will be the full string "+27 670742496"
        const { countryCode, phoneNumber } = parseContactString(value);

        // Update internal states if they differ from the parsed prop value
        if (countryCode !== currentCountryCode) {
            setCurrentCountryCode(countryCode);
        }
        if (phoneNumber !== currentPhoneNumber) {
            setCurrentPhoneNumber(phoneNumber);
        }
    }, [value]); // Rerun this effect when the 'value' prop changes

    // --- Helper to trigger onChange to parent Form.Item ---
    const triggerChange = (changedValue) => {
        // Combine the current state of both parts and pass as a single string to parent
        const newCountryCode = changedValue.countryCode !== undefined ? changedValue.countryCode : currentCountryCode;
        const newPhoneNumber = changedValue.phoneNumber !== undefined ? changedValue.phoneNumber : currentPhoneNumber;

        // Construct the full string exactly as expected by your display/backend
        const fullContactString = `${newCountryCode} ${newPhoneNumber}`;

        if (onChange) {
            onChange(fullContactString); // Pass the combined string back to the Form.Item
        }
    };

    // --- Handlers for internal components ---
    const handleAreaCodeChange = (selectedCodeValue) => {
        setCurrentCountryCode(selectedCodeValue);
        triggerChange({ countryCode: selectedCodeValue });
    };

    const handlePhoneNumberChange = (e) => {
        const newPhoneNumber = util.toNumeric(e.target.value); // Ensure it's numeric
        setCurrentPhoneNumber(newPhoneNumber);
        triggerChange({ phoneNumber: newPhoneNumber });
    };

    const handlePhoneNumberBlur = () => {
        // If the number becomes empty, you might want to send an empty string or 0
        if (currentPhoneNumber === '') {
            triggerChange({ phoneNumber: '' }); // Or whatever represents an empty number
        }
    }


    return (
        <div>
            <Input
                addonBefore={
                    <Select
                        style={{ width: 106 }}
                        onChange={handleAreaCodeChange}
                        value={currentCountryCode} // This must be controlled by state
                        showSearch // Allows searching through country codes
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {areaCodeList.map((code) => (
                            <Option key={code.value} value={code.value}>
                                {code.text}
                            </Option>
                        ))}
                    </Select>
                }
                className="contact-number"
                maxLength={12}
                minLength={6}
                onChange={handlePhoneNumberChange}
                value={currentPhoneNumber} // This must be controlled by state
                onBlur={handlePhoneNumberBlur}
            />
        </div>
    );
};